/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPIHousien } from "../core/OpenAPI";
import { request as __request } from "../core/request";

/**
 * The kind of listing copy the suggestion engine should produce.
 */
export type ListingSuggestionKind = "title" | "description";

/**
 * Request body for the listing copy suggestions endpoint.
 *
 * The agency is carried in the body (not the path): the backend evaluates the
 * caller's `view_property` permission against this `agency_id`.
 */
export type ListingSuggestionsRequest = {
  agency_id: string;
  kind: ListingSuggestionKind;
  property: Record<string, unknown>;
  count: number;
};

/**
 * Response payload for the listing copy suggestions endpoint.
 */
export type ListingSuggestionsResponse = {
  suggestions: Array<string>;
};

/**
 * ListingsCopyService class.
 *
 * Client for the internal, agency-agnostic listing copy suggestion endpoint.
 */
export class ListingsCopyService {
  /**
   * Suggest Listing Copy
   * Generate distinct listing title or description suggestions for a property.
   * @param requestBody The suggestion request (agency_id, kind, property fields, count).
   * @returns ListingSuggestionsResponse Suggestions generated successfully.
   * @throws ApiError
   */
  public static suggestListingCopy(
    requestBody: ListingSuggestionsRequest
  ): CancelablePromise<ListingSuggestionsResponse> {
    return __request(OpenAPIHousien, {
      method: "POST",
      url: "/listings/copy/suggestions",
      body: requestBody,
      mediaType: "application/json",
    });
  }
}
